import { Injectable, OnDestroy } from '@angular/core';
import { BackendApiService } from './backend-api';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { SystemConfiguration } from 'src/app/shared/models/database-models';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';

interface StatusData<T> {
  error: string;
  isLoading: boolean;
  data: T | null;
}

export enum SystemConfigurationCode {
  POCapitalBuyItemUnitPriceThreshold = 'PO_capitalbuyitemunitpricethreshold',
  GEWCALicenseExpiry = 'NoDaysPriorGEWCALicenseExpiry',
}

const INITIAL_STATE: StatusData<SystemConfiguration[]> = {
  error: '',
  isLoading: false,
  data: null,
};

@Injectable({ providedIn: 'root' })
export class SystemConfigurationService implements OnDestroy {
  private systemConfigurations = new BehaviorSubject<
    StatusData<SystemConfiguration[]>
  >(INITIAL_STATE);
  public systemConfigurations$ = this.systemConfigurations.asObservable();

  private apiSubscription?: Subscription;
  private readonly codes: string[] = Object.values(SystemConfigurationCode);

  constructor(private backendApi: BackendApiService) {
    this.onFetchAll();
  }

  public get systemConfigurationsData() {
    return this.systemConfigurations.value.data;
  }

  public getSystemConfiguration(
    code: SystemConfigurationCode
  ): SystemConfiguration | null {
    const data = this.systemConfigurationsData;
    if (!Array.isArray(data)) {
      return null;
    }
    const systemConfig = data.find((config) => config.Code === code);

    return systemConfig ?? null;
  }

  public onFetchAll(): void {
    this.systemConfigurations.next({
      ...this.systemConfigurations.value,
      error: '',
      isLoading: true,
    });

    const subscription = this.backendApi
      .getSystemConfigurations(this.codes)
      .subscribe({
        next: (response): void => {
          this.systemConfigurations.next({
            error: '',
            isLoading: false,
            data: response,
          });
        },
        error: (errMsg: string): void => {
          this.systemConfigurations.next({
            error: errMsg ?? 'Failed to retrieve information.',
            isLoading: false,
            data: null,
          });
        },
      });

    this.apiSubscription = subscription;
  }

  ngOnDestroy(): void {
    if (this.apiSubscription?.unsubscribe) {
      this.apiSubscription.unsubscribe();
    }
  }
}
